import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * Cart 2.0 — In-cart subscription conversion (native Shopify selling plans).
 *
 * Converts a cart line between one-time and a subscription selling plan, and
 * changes frequency, entirely through /cart/change.js — no page reload, no
 * Recharge widget/SDK/token. The line is identified by its 1-based `line` index
 * (required by Shopify for selling-plan changes). After the change we dispatch a
 * CartUpdateEvent so the existing <cart-items-component> morphs the drawer; the
 * control's state is re-derived from the rendered selling_plan_allocation, so
 * there is no parallel JS state to keep in sync.
 *
 * @typedef {object} Refs
 * @property {HTMLSelectElement} [select] - Frequency select (subscribed lines).
 * @property {HTMLButtonElement} [subscribeButton] - Subscribe CTA (one-time lines).
 * @extends {Component<Refs>}
 */
class CartLineSubscription extends Component {
  /** @type {boolean} */
  #loading = false;

  /** @returns {number} 1-based cart line index. */
  get line() {
    return Number(this.dataset.line);
  }

  /** @returns {number} Current line quantity (preserved across the change). */
  get quantity() {
    return Number(this.dataset.quantity || 1);
  }

  /**
   * One-time → subscription using the line's default selling plan.
   * @param {Event} event
   */
  onSubscribe(event) {
    const target = event?.target instanceof Element ? event.target.closest('[data-selling-plan-id]') : null;
    const planId = (target ?? this.refs.subscribeButton)?.dataset.sellingPlanId;
    if (planId) this.#change(planId);
  }

  /**
   * Frequency change, or subscription → one-time (empty value).
   * @param {Event} event
   */
  onChange(event) {
    const value = event?.target instanceof HTMLSelectElement ? event.target.value : (this.refs.select?.value ?? '');
    this.#change(value === '' ? null : value);
  }

  /**
   * Apply a selling plan (or null to remove it) to this line and re-render.
   * @param {string | null} sellingPlan
   */
  #change(sellingPlan) {
    if (this.#loading) return;
    this.#loading = true;
    this.classList.add('cart-subscribe--loading');

    const sectionIds = new Set();
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    }

    const body = JSON.stringify({
      line: this.line,
      quantity: this.quantity,
      selling_plan: sellingPlan ? Number(sellingPlan) : null,
      sections: Array.from(sectionIds).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.json())
      .then((parsed) => {
        if (parsed.errors) {
          console.error('[cart-line-subscription]', parsed.errors);
          return;
        }

        // Re-render via the existing cart pipeline. <cart-items-component> hears
        // this on the document and morphs from data.sections; line indices and the
        // selected option are re-derived from the freshly rendered cart.
        this.dispatchEvent(
          new CartUpdateEvent(parsed, Array.from(sectionIds)[0] ?? 'cart', {
            source: 'cart-line-subscription',
            itemCount: parsed.item_count,
            sections: parsed.sections,
          })
        );
      })
      .catch((error) => console.error('[cart-line-subscription]', error))
      .finally(() => {
        this.#loading = false;
        this.classList.remove('cart-subscribe--loading');
      });
  }
}

if (!customElements.get('cart-line-subscription')) {
  customElements.define('cart-line-subscription', CartLineSubscription);
}
